// The form holds a list operand bare; the wire wraps it as `{ values }`.
export type ContextOperand = string | number | boolean | readonly unknown[] | { values?: readonly unknown[] } | null;

export type ContextPredicate = Record<string, ContextOperand | undefined>;

export interface ContextClauseShape {
  predicates?: Record<string, unknown>;
}

export interface ContextConjunctionShape {
  clauses?: ContextClauseShape[];
}

export interface ContextFilterShape {
  conjunctions?: ContextConjunctionShape[];
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

export function hasContextOperand(operand: ContextOperand | undefined): boolean {
  if (Array.isArray(operand)) {
    return operand.length > 0;
  }

  if (isRecord(operand)) {
    const { values } = operand as { values?: readonly unknown[] };

    return Array.isArray(values) && values.length > 0;
  }

  return operand !== undefined && operand !== null && operand !== '';
}

function isCompletePredicate(value: unknown): boolean {
  if (!isRecord(value)) {
    return false;
  }

  const operators = Object.keys(value).filter((key) => key !== 'not');

  return operators.length === 1 && hasContextOperand(value[operators[0]] as ContextOperand);
}

export function isWellFormedContextFilter(contextFilter?: ContextFilterShape | null): boolean {
  if (contextFilter == null) {
    return true;
  }

  const { conjunctions } = contextFilter;

  if (!Array.isArray(conjunctions) || conjunctions.length === 0) {
    return false;
  }

  return conjunctions.every(({ clauses }) => {
    if (!Array.isArray(clauses) || clauses.length === 0) {
      return false;
    }

    return clauses.every(({ predicates }) => {
      if (!isRecord(predicates)) {
        return false;
      }

      const rows = Object.values(predicates);

      return rows.length > 0 && rows.every(isCompletePredicate);
    });
  });
}
